import React, { useEffect, useState } from 'react';
import { FormApi, FormState } from 'final-form';

import Leasehold from 'src/models/leasehold/Leasehold';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import Ownership from 'src/models/tract_detail/Ownership';

import CardContainer from 'src/components/common/CardContainer.redesign';
import OwnershipCard from './OwnershipCard';
import _ from 'lodash';
import Owner from 'src/models/tract_detail/Owner';
import { SelectItem } from 'src/models/common/SelectItem';
import Button from 'src/components/controls/forms/redesign/FormButton';
import DialogList from 'src/dialogs';
import LeaseholdPicker from './LeaseholdPicker';
import { AnyObject, FormSpy } from 'react-final-form';
import PropertyUtil from 'src/util/PropertyUtil';
import PagedResultSet from 'src/models/common/PagedResultSet';
import MathUtil from 'src/util/MathUtil';
import ProjectStaff from 'src/models/ProjectStaff';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';

interface IOwnershipListStateProps {
    clientNumber: number;
    projectNumber: number;
    sectionNumber: number;
    tractNumber: number;
    horizonNumber: number;
    horizon: number;
    form: FormApi;
    leaseholds: Array<Leasehold>;
    ownershipContactCategories: Array<OwnershipContactCategory>;
    ownerships: PagedResultSet<Ownership>;
    totalInterest: number;
    executiveRights: number;
    owners: Array<Owner>;
    grossAcres: number;
    projectStaff: PagedResultSet<ProjectStaff>;
    errorMessage: string;
    hasNonExecutiveAdditionalOwnership: boolean;
    idVerificationMessage: string;
    idVerificationResult: boolean;

    navigate: (path: string) => void;
    showOwnerSearch: (dialogName: string) => void;
    getPagedOwnerships: (pageNumber: number) => void;
    setDialogShowing: (dialogName: string) => void;
    getLeaseholds: (ownershipId: number) => void;
    getLeaseholdsByTract: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number) => void;
    getLeaseholdsForDelete: (ownershipId: number) => void;
    hideDialog: () => void;
    addAdditionalOwnership: (parentOwnershipId: number) => void;
    deleteAdditionalOwnership: (additionalOwnershipId: number) => void;
    openOwnershipNotes: (ownershipId: number) => void;
    deleteOwnership: (ownershipId: number) => void;
    promoteAdditionalOwnership: (ownershipId: number) => void;
    clearError: () => void;
    initOwnerTracts: () => void;
    clearVerification: () => void;
    setVerificationMessage: (message: string) => void;
    cloneData: (context: string, options: string) => void;
    incrementOwnershipPage: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number, horizonNumber: number) => void;
    navigateAcquisition: (ownershipId: number, ownerLookupId: number) => void;
    openContactNotes: (ownership: Ownership) => void;
    onAddOwnership: () => void;
}

const OwnershipList = (props: IOwnershipListStateProps) => {
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const [selectedOwnershipId, setSelectedOwnershipId] = useState(undefined);
    const [interestStatus, setInterestStatus] = useState('good');
    const [rightsStatus, setRightsStatus] = useState('good');
    const [totalInterest, setTotalInterest] = useState(props.totalInterest);
    const [totalAcreage, setTotalAcreage] = useState('0');
    const [initialInterests, setInitialInterests] = useState([]);
    const [initialExecutiveRights, setInitialExecutiveRights] = useState([]);
    const [deltaInterests, setDeltaInterests] = useState([]);
    const [deltaExecutiveRights, setDeltaExecutiveRights] = useState([]);
    const [showLeaseButton, setShowLeaseButton] = useState<boolean>(true);
    const [showCategoryButton, setShowCategoryButton] = useState<boolean>(true);

    useEffect(() => {
        calcTotalInterest();
        calcTotalAcres();
    }, []);

    useEffect(() => {
        if (!totalInterest || totalInterest === 0) {
            calcTotalInterest();
        }
        if (!!props.grossAcres) {
            calcTotalAcres();
        }
    }, [props.grossAcres, totalInterest]);

    useEffect(() => {
        if (deltaInterests?.length > 0) {
            calcTotalInterest();
            calcTotalAcres();
        }
    }, [deltaInterests]);

    // tslint:disable-next-line:no-any
    const isNumber = (testSubject: any) => {
        return !isNaN(parseFloat(testSubject)) && isFinite(testSubject);
    };

    const onFormChange = (fs: FormState<AnyObject>, form: FormApi) => {
        const regex = /[0-9]/g;

        if (fs.active) {
            const recordIndex = PropertyUtil.SafeGet(() => fs.active.match(regex)[0]);

            if (fs.active.includes('decimalInterest') && PropertyUtil.SafeGet(() => isNumber(fs?.values?.ownerships[recordIndex]?.decimalInterest))) {
                if (fs.active.includes('additionalOwnerships')) {
                    const additionalOwnershipIndex = PropertyUtil.SafeGet(() => fs.active.match(regex)[1]);
                    // tslint:disable-next-line: max-line-length
                    form.change(
                        `ownerships[${recordIndex}].additionalOwnerships[${additionalOwnershipIndex}].netAcres`,
                        MathUtil.cleanNumber(
                            fs.values.ownerships[recordIndex].additionalOwnerships[additionalOwnershipIndex].decimalInterest * props.grossAcres
                        )
                    );
                } else {
                    form.change(
                        `ownerships[${recordIndex}].netAcres`,
                        MathUtil.cleanNumber(fs.values.ownerships[recordIndex].decimalInterest * props.grossAcres)
                    );
                }
            } else if (fs.active.includes('netAcres') && isNumber(fs.values.ownerships[recordIndex].netAcres)) {
                if (fs.active.includes('additionalOwnerships')) {
                    const additionalOwnershipIndex = PropertyUtil.SafeGet(() => fs.active.match(regex)[1]);
                    // tslint:disable-next-line: max-line-length
                    form.change(
                        `ownerships[${recordIndex}].additionalOwnerships[${additionalOwnershipIndex}].decimalInterest`,
                        MathUtil.cleanNumber(fs.values.ownerships[recordIndex].additionalOwnerships[additionalOwnershipIndex].netAcres / props.grossAcres)
                    );
                } else {
                    form.change(
                        `ownerships[${recordIndex}].decimalInterest`,
                        MathUtil.cleanNumber(fs.values.ownerships[recordIndex].netAcres / props.grossAcres)
                    );
                }
            }
        }
    };

    const onNavigateLeaseInfo = (ownershipId: number) => {
        setSelectedOwnershipId(ownershipId);
        props.getLeaseholds(ownershipId);
    };

    const onDeleteOwnership = (ownershipId: number) => {
        setSelectedOwnershipId(ownershipId);
        props.getLeaseholdsForDelete(ownershipId);
    };

    const onMakeMineralOwnership = (ownershipId: number) => {
        props.promoteAdditionalOwnership(ownershipId);
    };

    const addInitialInterestItem = (event: React.FocusEvent<HTMLInputElement>) => {
        const activeField = props.form.getState().active;

        const regexDecimalInterest = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?decimalInterest/gm;
        const regexExecutiveRights = /ownerships\[[0-9]*\]\.executiveRights/gm;
        const regexNetAcres = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?netAcres/gm;
        const regexIndex = /\[[0-9]*\]/gm;

        if (regexDecimalInterest.exec(activeField) || regexNetAcres.exec(activeField) || regexExecutiveRights.exec(activeField)) {
            const stringMatches = activeField.match(regexIndex);

            let ownership: Ownership = undefined;
            if (stringMatches.length === 1) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex];
            } else if (stringMatches.length === 2) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                const addOwnershipIndex = parseInt(stringMatches[1].substring(1, stringMatches[1].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex].additionalOwnerships[addOwnershipIndex];
            } else {
                return;
            }

            if (ownership) {
                if (!initialInterests.find((x) => x.id === ownership.id)) {
                    setInitialInterests([
                        ...initialInterests,
                        {
                            id: ownership.id,
                            value: ownership.decimalInterest,
                        },
                    ]);
                }

                if (!initialExecutiveRights.find((x) => x.id === ownership.id)) {
                    setInitialExecutiveRights([
                        ...initialExecutiveRights,
                        {
                            id: ownership.id,
                            value: ownership.executiveRights,
                        },
                    ]);
                }
            }
        }
    };

    const updateDeltaInterestItem = (event: React.FocusEvent<HTMLInputElement>) => {
        const activeField = props.form.getState().active;

        const regexDecimalInterest = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?decimalInterest/gm;
        const regexNetAcres = /ownerships\[[0-9]*\]\.(additionalOwnerships\[[0-9]*\]\.)?netAcres/gm;
        const regexExecutiveRights = /ownerships\[[0-9]*\]\.executiveRights/gm;
        const regexIndex = /\[[0-9]*\]/gm;

        if (regexDecimalInterest.exec(activeField) || regexNetAcres.exec(activeField) || regexExecutiveRights.exec(activeField)) {
            const stringMatches = activeField.match(regexIndex);

            let ownership: Ownership = undefined;
            if (stringMatches.length === 1) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex];
            } else if (stringMatches.length === 2) {
                const ownershipIndex = parseInt(stringMatches[0].substring(1, stringMatches[0].length - 1), 10);
                const addOwnershipIndex = parseInt(stringMatches[1].substring(1, stringMatches[1].length - 1), 10);
                ownership = (props.form.getState().values as AnyObject).ownerships[ownershipIndex].additionalOwnerships[addOwnershipIndex];
            } else {
                return;
            }

            if (ownership) {
                let existingDelta = deltaInterests.find((x) => x.id === ownership.id);

                if (!!existingDelta) {
                    const originalOwnerInterest = initialInterests.find((x) => x.id === ownership.id);
                    const deltaInterest = ownership.decimalInterest - parseFloat(originalOwnerInterest.value);

                    setDeltaInterests([
                        ...deltaInterests.filter((x) => x.id !== ownership.id),
                        {
                            id: ownership.id,
                            value: deltaInterest,
                        },
                    ]);
                } else {
                    // if doesn't exist do this
                    const originalOwnerInterest = initialInterests.find((x) => x.id === ownership.id).value;
                    const deltaInterest = ownership.decimalInterest - originalOwnerInterest;
                    setDeltaInterests([
                        ...deltaInterests,
                        {
                            id: ownership.id,
                            value: deltaInterest,
                        },
                    ]);
                }

                if (!deltaExecutiveRights.find((x) => x.id === ownership.id)) {
                    const originalExecutiveRights = initialExecutiveRights.find((x) => x.id === ownership.id).value;
                    const deltaExecutiveRight = ownership.executiveRights - originalExecutiveRights;
                    setDeltaExecutiveRights([
                        ...deltaExecutiveRights,
                        {
                            id: ownership.id,
                            value: deltaExecutiveRight,
                        },
                    ]);
                }

                if (deltaExecutiveRights.find((x) => x.id === ownership.id)) {
                    const originalExecutiveRights = initialExecutiveRights.find((x) => x.id === ownership.id);
                    const deltaExecutiveRight = ownership.executiveRights - parseFloat(originalExecutiveRights.value);

                    setDeltaExecutiveRights([
                        ...deltaExecutiveRights.filter((x) => x.id !== ownership.id),
                        {
                            id: ownership.id,
                            value: deltaExecutiveRight,
                        },
                    ]);
                }
            }
        }
    };

    const calcTotalInterest = () => {
        const newTotalInterest = props.totalInterest ? props.totalInterest : 0;
        const calculatedInterest = newTotalInterest + deltaInterests.reduce((acc, current) => acc + current.value, 0);
        let formattedInterest = parseFloat(MathUtil.cleanNumber(calculatedInterest));

        // need to calculate with better precision
        if (formattedInterest < 1 && interestStatus !== 'low') {
            setInterestStatus('low');
        } else if (formattedInterest > 1 && interestStatus !== 'high') {
            setInterestStatus('high');
        } else if (formattedInterest === 1 && interestStatus !== 'good') {
            setInterestStatus('good');
        }
        setTotalInterest(formattedInterest);
    };

    const calcTotalAcres = () => {
        if (!Number.isNaN(totalInterest)) {
            setTotalAcreage(MathUtil.cleanNumber(props.grossAcres ? totalInterest * props.grossAcres : 0));
        } else {
            setTotalAcreage('Please enter a valid number');
        }
    };

    const totalExecutiveRights = () => {
        const newExecutiveRights = props.executiveRights ? props.executiveRights : 0;
        const calculatedExecutiveRights = newExecutiveRights + deltaExecutiveRights.reduce((acc, current) => acc + current.value, 0);

        // need to calculate with better precision
        if (calculatedExecutiveRights < 1 && rightsStatus !== 'low') {
            setRightsStatus('low');
        } else if (calculatedExecutiveRights > 1 && rightsStatus !== 'high') {
            setRightsStatus('high');
        } else if (calculatedExecutiveRights === 1 && rightsStatus !== 'good') {
            setRightsStatus('good');
        }

        return calculatedExecutiveRights.toString();
    };

    // tslint:disable-next-line:max-line-length
    const ownershipContactCategoryItems =
        props.ownershipContactCategories && props.ownershipContactCategories.length > 0
            ? props.ownershipContactCategories.map((type: OwnershipContactCategory) => new SelectItem(type.id, type.name))
            : undefined;

    const leaseBuyers =
        props.projectStaff && props.projectStaff.items.length > 0
            ? props.projectStaff.items
                  .map((landman: ProjectStaff) => new SelectItem(landman.id, landman.user.firstName + ' ' + landman.user.lastName))
            : undefined;

    const additionalOwnerships = (ownershipId: number) => {
        const temp = props.ownerships.items
            .map((ownership, index) => {
                if (ownership.parentOwnershipId === ownershipId) {
                    return { ownership, index };
                } else {
                    return undefined;
                }
            })
            .filter(Boolean);

        return temp;
    };

    const incrementOwnershipPage = (
        clientNumber: number,
        projectNumber: number,
        tractNumber: number,
        sectionNumber: number,
        horizonNumber: number
    ) => {
        props.incrementOwnershipPage(props.clientNumber, props.projectNumber, props.sectionNumber, props.tractNumber, props.horizonNumber);
    };

    const card = (ownership: Ownership, index: number) => {
        return (
            <div key={ownership.id}>
                <OwnershipCard
                    ownership={ownership}
                    owners={props.owners}
                    index={index}
                    contactCategories={ownershipContactCategoryItems}
                    additionalOwnerships={additionalOwnerships(ownership.id)}
                    addAdditionalOwnership={props.addAdditionalOwnership}
                    deleteAdditionalOwnerships={props.deleteAdditionalOwnership}
                    navigateLeaseInfo={onNavigateLeaseInfo}
                    openOwnershipNotes={props.openOwnershipNotes}
                    deleteOwnership={onDeleteOwnership}
                    makeMineralOwnership={onMakeMineralOwnership}
                    leaseBuyers={leaseBuyers}
                    onInterestFocus={addInitialInterestItem}
                    onInterestChange={updateDeltaInterestItem}
                    hasNonExecutiveAdditionalOwnership={props.hasNonExecutiveAdditionalOwnership}
                    navigateAcquisition={props.navigateAcquisition}
                    openContactNotes={props.openContactNotes}
                />
            </div>
        );
    };

    const cardContainer = () => {
        const { ownerships } = props;
        let isOnLastPage = false;
        if (ownerships) {
            isOnLastPage = ownerships.pageNumber * ownerships.pageSize < ownerships.totalRecords;
        }

        if (!ownerships || ownerships.totalRecords <= 0) {
            return <div>No Results Found</div>;
        }

        return (
            <>
                <FormSpy onChange={(e: FormState<AnyObject>) => onFormChange(e, props.form)} subscription={{ active: true, values: true }} />
                <CardContainer
                    // style={{ overflowY: 'scroll' }}
                    showNextPagePanel={isOnLastPage}
                    nextPageAction={() =>
                        incrementOwnershipPage(props.clientNumber, props.projectNumber, props.sectionNumber, props.tractNumber, props.horizonNumber)
                    }
                >
                    {ownerships.items.map(card)}
                </CardContainer>
            </>
        );
    };

    const showManageLeasehold = () => {
        props.setDialogShowing(DialogList.MANAGE_LEASEHOLD);
        props.getLeaseholdsByTract(props.clientNumber, props.projectNumber, props.sectionNumber, props.tractNumber);
    };

    const showAddOwnership = () => {
        props.initOwnerTracts();
        props.onAddOwnership();
    };

    const handleSelectedId = (e: AnyObject, field: 'Lease Buyer' | 'Contact Category') => {
        const currentForm: AnyObject = props.form.getState().values;
        const ownershipList = currentForm.ownerships;

        ownershipList.forEach((o: Ownership, index: number) => {
            if (field === 'Lease Buyer') {
                props.form.change(`ownerships[${index}].leaseBuyer`, parseInt(e.target.value + '', 10));
            } else if (field === 'Contact Category') {
                props.form.change(`ownerships[${index}].ownershipContactCategoryId`, parseInt(e.target.value + '', 10));
            }
        });
    };

    const leaseBuyerDropdown = () => {
        return (
            <RHFSelect
                label="Lease Buyer"
                name=""
                options={
                    !!leaseBuyers
                        ? leaseBuyers.map((l) => {
                              return { value: l.id, label: `${l.name}` };
                          })
                        : []
                }
                onChange={(e) => handleSelectedId(e, 'Lease Buyer')}
            />
        );
    };

    const contactCategoryDropdown = () => {
        return (
            <RHFSelect
                label="Contact Category"
                name=""
                options={
                    !!props?.ownershipContactCategories
                        ? props.ownershipContactCategories.map((cc) => {
                              return { value: cc.id, label: `${cc.name}` };
                          })
                        : []
                }
                onChange={(e) => handleSelectedId(e, 'Contact Category')}
            />
        );
    };

    const onCopyOwnership = () => {
        dialogContext.leasehold.copyOwnership(
            'ownership', 
            { projectNumber: props.projectNumber, clientNumber: props.clientNumber },
            massageCloneData
        );
    };

    const hideButton = (buttonName: 'Lease Buyer' | 'Contact Category') => {
        if (buttonName === 'Lease Buyer') {
            setShowLeaseButton(false);
        } else if (buttonName === 'Contact Category') {
            setShowCategoryButton(false);
        }
    };

    const massageCloneData = (context: string, options: string) => {
        const current = `${props.clientNumber}.${props.projectNumber}.${props.sectionNumber}.${props.tractNumber}-${props.horizonNumber}`;

        const newOptions = JSON.parse(options);
        newOptions.targetHorizon = current;

        props.cloneData(context, JSON.stringify(newOptions));
    };

    return (
        <div className="ownership-list" style={{ overflow: 'auto' }}>
            <div className="ownership-list-heading">
                <div style={{ display: 'flex' }}>
                    {props.hasNonExecutiveAdditionalOwnership && (
                        <div style={{ margin: '.5rem', padding: '.5rem' }}>
                            <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Executive Rights</div>
                            <div className={`interest-status-${rightsStatus}`} style={{ padding: '.5rem', marginTop: '.5rem' }}>
                                {parseFloat(MathUtil.cleanNumber(totalExecutiveRights(), 7))}
                            </div>
                        </div>
                    )}
                    <div style={{ margin: '.5rem', padding: '.5rem' }}>
                        <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Interest</div>
                        <div className={`interest-status-${interestStatus}`} style={{ padding: '.5rem', marginTop: '.5rem' }}>
                            {!Number.isNaN(totalInterest) ? MathUtil.cleanNumber(totalInterest) : 'Please enter a valid number'}
                        </div>
                    </div>
                    <div style={{ margin: '.5rem', padding: '.5rem' }}>
                        <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Acreage</div>
                        <div className={`interest-status-${interestStatus}`} style={{ padding: '.5rem', marginTop: '.5rem' }}>
                            {totalAcreage}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '1rem', marginRight: '1rem' }}>
                        {showCategoryButton && (
                            <Button
                                text="Set Owners' Contact Category"
                                size="medium"
                                intent="primary"
                                onClick={() => hideButton('Contact Category')}
                                disabled={props?.ownershipContactCategories?.length === 0 ? true : false}
                            />
                        )}
                        {!showCategoryButton && contactCategoryDropdown()}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginRight: '1rem' }}>
                        {showLeaseButton && (
                            <Button
                                text="Set Owners' Lease Buyer"
                                size="medium"
                                intent="primary"
                                onClick={() => hideButton('Lease Buyer')}
                                disabled={leaseBuyers?.length === 0 ? true : false}
                            />
                        )}
                        {!showLeaseButton && leaseBuyerDropdown()}
                    </div>
                    <Button
                        text="Copy Ownership"
                        size="medium"
                        intent="primary"
                        onClick={() => onCopyOwnership()}
                        disabled={props?.form?.getState()?.dirty}
                    />
                    <Button
                        className="ml-1"
                        text="Attach Ownership"
                        size="medium"
                        intent="primary"
                        onClick={showAddOwnership}
                        disabled={props?.form?.getState()?.dirty}
                    />
                    <div style={{ display: 'none' }}>
                        <Button
                            text="Manage Leasehold"
                            className="ml-1"
                            size="medium"
                            intent="primary"
                            onClick={showManageLeasehold}
                            disabled={props?.form?.getState()?.dirty}
                        />
                    </div>
                </div>
            </div>
            {props.ownerships && cardContainer()}

            <LeaseholdPicker
                ownershipId={selectedOwnershipId}
                navigate={props.navigate}
                leaseholds={props.leaseholds}
                getLeaseholds={props.getLeaseholds}
                hideDialog={() => {
                    props.hideDialog();
                    props.clearVerification();
                }}
            />
        </div>
    );
};

export default OwnershipList;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/OwnershipList.tsx